/**
 * Helpers de importación de archivos (Excel/CSV y OCR).
 *
 * Estaban embebidos en `src/pages/GestionCostosImportar.jsx`. Se extrajeron
 * acá para que el importador de listas de precio por proveedor use el mismo
 * parseo, la misma normalización y el mismo matching, en vez de una copia que
 * después diverge con el primer bug que se arregla en un solo lado.
 *
 * IMPORTANTE: el comportamiento es idéntico al original. Si algo de esto se
 * modifica, se modifica para los dos importadores a la vez.
 */

/** Minúsculas, sin acentos, sin signos, espacios colapsados. Base del matching por nombre. */
export const normalize = (s) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s-]/g, '');

/**
 * Number tolerante al formato argentino y al anglosajón:
 * "1.234,56" → 1234.56 · "1,234.56" → 1234.56 · "1234,5" → 1234.5
 */
export const toNumber = (v) => {
  if (v === null || v === undefined) return 0;
  const raw = String(v).trim();
  if (!raw) return 0;

  const hasComma = raw.includes(',');
  const hasDot = raw.includes('.');

  let s = raw;
  if (hasComma && hasDot) s = s.replaceAll('.', '').replaceAll(',', '.');
  else if (hasComma && !hasDot) s = s.replaceAll(',', '.');

  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

export const money = (n) =>
  Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const MAX_OCR_FILE_SIZE_MB = 8;

export const isPdfFile = (file) =>
  String(file?.type || '').includes('pdf') || String(file?.name || '').toLowerCase().endsWith('.pdf');

export const isAllowedOcrFile = (file) =>
  String(file?.type || '').startsWith('image/') || isPdfFile(file);

export const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

/**
 * Detección heurística de columnas:
 * - match: barcode/codigo/ean/gtin, sino nombre/descripcion
 * - cost: costo/cost/price/precio/proveedor/neto
 */
export const guessColumns = (headers) => {
  const hs = headers.map((h) => normalize(h));

  const findFirst = (patterns) => {
    for (const p of patterns) {
      const idx = hs.findIndex((h) => h.includes(p));
      if (idx >= 0) return headers[idx];
    }
    return null;
  };

  const matchCol =
    findFirst(['barcode', 'codigo de barra', 'codigo barra', 'codigo', 'codbarras', 'ean', 'gtin']) ||
    findFirst(['sku']) ||
    findFirst(['nombre', 'descripcion', 'producto', 'articulo', 'item']) ||
    headers[0] ||
    null;

  const costCol =
    findFirst(['costo', 'cost']) ||
    findFirst(['precio proveedor', 'precio', 'price', 'neto', 'lista']) ||
    headers[1] ||
    headers[0] ||
    null;

  return { matchCol, costCol };
};

/**
 * Borrador de importación en sessionStorage, por organización.
 *
 * Cada importador usa su propia `baseKey`: el borrador del importador de
 * costos y el de listas de proveedor no se pisan entre sí.
 */
export const createImportDraftStorage = (baseKey) => {
  const getKey = (orgId) => `${baseKey}:${orgId || 'anon'}`;

  return {
    read: (orgId) => {
      if (!orgId || typeof window === 'undefined') return null;
      try {
        const raw = window.sessionStorage.getItem(getKey(orgId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    },
    write: (orgId, payload) => {
      if (!orgId || typeof window === 'undefined') return;
      try {
        window.sessionStorage.setItem(getKey(orgId), JSON.stringify(payload));
      } catch {
        // Si el navegador rechaza el guardado, seguimos sin romper el flujo.
      }
    },
    clear: (orgId) => {
      if (!orgId || typeof window === 'undefined') return;
      try {
        window.sessionStorage.removeItem(getKey(orgId));
      } catch {
        // Sin impacto funcional.
      }
    },
  };
};

// Borrador del importador de costos. Se mantiene la clave original para no
// perder los borradores que los usuarios ya tengan guardados.
const costosDraftStorage = createImportDraftStorage('impulza:gestion-costos-importar:draft:v1');

export const readImportDraft = costosDraftStorage.read;
export const writeImportDraft = costosDraftStorage.write;
export const clearImportDraft = costosDraftStorage.clear;

// Borrador del importador de listas de precio por proveedor.
export const listaProveedorDraftStorage = createImportDraftStorage(
  'impulza:importar-lista-proveedor:draft:v1'
);
